// src/components/DevicesView.tsx
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Device } from '../types';
import { SonarIcon, DroneIcon, CameraIcon, XIcon } from './icons';
import RoddoPageShell from './layout/RoddoPageShell';
import RoddoSectionCard from './layout/RoddoSectionCard';


type DevicesViewProps = {
  userProfile?: any;
  isDarkMode?: boolean;
  onUpgradeToPro?: () => void;
};



const allAvailableDevices: Device[] = [
  { id: 'garmin_striker', name: 'Garmin Striker Vivid 7sv', category: 'Sonar' },
  { id: 'humminbird_helix', name: 'Humminbird HELIX 9', category: 'Sonar' },
  { id: 'aquavu_hd7i', name: 'Aqua-Vu HD7i', category: 'Camera' },
  { id: 'gofish_cam', name: 'GoFish Cam Wireless', category: 'Camera' },
  { id: 'dji_mavic', name: 'DJI Mavic 3 Pro', category: 'Drone' },
  { id: 'powerray_wizard', name: 'PowerRay Wizard', category: 'Drone' },
];

const DevicesView: React.FC<DevicesViewProps> = ({ userProfile, isDarkMode, onUpgradeToPro }) => {
  const { t } = useTranslation();
  const [connectedDevices, setConnectedDevices] = useState<Device[]>([]);
  const [availableDevices, setAvailableDevices] = useState<Device[]>([]);
  const [isScanning, setIsScanning] = useState(false);
  const [showFeedModal, setShowFeedModal] = useState<Device | null>(null);

  const handleScan = () => {
    setIsScanning(true);
    setAvailableDevices([]); // Clear previous scan
    setTimeout(() => {
      // Filter out already connected devices from the scan result
      const newAvailable = allAvailableDevices.filter(
        d => !connectedDevices.some(cd => cd.id === d.id),
      );
      setAvailableDevices(newAvailable);
      setIsScanning(false);
    }, 2000); // Simulate a 2-second scan
  };

  const connectDevice = (device: Device) => {
    setConnectedDevices(prev => [...prev, device]);
    setAvailableDevices(prev => prev.filter(d => d.id !== device.id));
  };

  const disconnectDevice = (device: Device) => {
    setConnectedDevices(prev => prev.filter(d => d.id !== device.id));
    setAvailableDevices(prev =>
      [device, ...prev].sort((a, b) => a.name.localeCompare(b.name)),
    );
  };

  const getDeviceIcon = (category: Device['category']) => {
    switch (category) {
      case 'Sonar':
        return (
          <SonarIcon className="w-6 h-6 text-primary dark:text-[#67D8E8]" />
        );
      case 'Camera':
        return (
          <CameraIcon className="w-6 h-6 text-primary dark:text-[#67D8E8]" />
        );
      case 'Drone':
        return (
          <DroneIcon className="w-6 h-6 text-primary dark:text-[#67D8E8]" />
        );
    }
  };

  const renderDeviceList = (devices: Device[], isConnectedList: boolean) => {
    const sonars = devices.filter(d => d.category === 'Sonar');
    const cameras = devices.filter(d => d.category === 'Camera');
    const drones = devices.filter(d => d.category === 'Drone');

    const renderCategory = (title: string, deviceList: Device[]) =>
      deviceList.length > 0 && (
        <div key={title}>
          <h4 className="font-semibold text-slate-500 dark:text-slate-400 text-sm mb-2">
            {title}
          </h4>
          <div className="space-y-2">
            {deviceList.map(device => (
              <div
                key={device.id}
                className="p-3 bg-white/90 dark:bg-[#101827] rounded-[14px] shadow-sm border border-white/60 dark:border-[rgba(132,151,190,0.14)]"
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center space-x-3">
                    {getDeviceIcon(device.category)}
                    <span className="font-medium text-sm text-slate-800 dark:text-slate-100">
                      {device.name}
                    </span>
                  </div>
                  {isConnectedList ? (
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setShowFeedModal(device)}
                        className="text-[11px] font-semibold text-white px-3 py-1 rounded-full bg-[#2F80ED] dark:bg-[#67D8E8] dark:text-[#07131A] hover:bg-opacity-90 shadow-sm"
                      >
                        {t('devices.liveFeed', 'Live feed')}
                      </button>
                      <button
                        onClick={() => disconnectDevice(device)}
                        className="text-[11px] font-semibold text-danger px-3 py-1 rounded-full bg-red-100 dark:bg-red-900/50 hover:bg-red-200"
                      >
                        {t('devices.disconnect', 'Disconnect')}
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => connectDevice(device)}
                      className="text-[11px] font-semibold text-white px-3 py-1 rounded-full bg-[#2F80ED] dark:bg-[#67D8E8] dark:text-[#07131A] hover:bg-opacity-90 shadow-sm"
                    >
                      {t('devices.connect', 'Connect')}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      );

    return (
      <div className="space-y-4">
        {renderCategory(t('devices.sonars', 'Sonar & fish finders'), sonars)}
        {renderCategory(t('devices.cameras', 'Underwater cameras'), cameras)}
        {renderCategory(t('devices.drones', 'Drones & aerial'), drones)}
      </div>
    );
  };

  const LiveFeedModal: React.FC<{ device: Device; onClose: () => void }> = ({
    device,
    onClose,
  }) => (
    <div className="fixed inset-0 bg-black/80 z-[1200] flex items-center justify-center p-4 backdrop-blur-sm">
      <div className="bg-neutral-dark rounded-[18px] shadow-xl w-full max-w-md p-4 space-y-3 relative border border-white/10">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 p-1.5 rounded-full bg-black/40 hover:bg-black/60"
        >
          <XIcon className="w-5 h-5 text-white" />
        </button>
        <div className="aspect-video bg-black rounded-[14px] overflow-hidden flex items-center justify-center relative">
          {device.category === 'Sonar' && (
            <div className="w-full h-full p-2 bg-blue-900 text-cyan-300 font-mono text-[10px] overflow-hidden relative">
              <div className="absolute inset-0 w-full h-full bg-grid-pattern opacity-30" />
              <div className="absolute top-2 left-2 space-y-1">
                <p>
                  DEPTH:{' '}
                  <span className="text-white">
                    {(40 + Math.random() * 10).toFixed(1)}ft
                  </span>
                </p>
                <p>
                  TEMP: <span className="text-white">68.4°F</span>
                </p>
              </div>
              <svg
                width="100%"
                height="100%"
                className="absolute bottom-0 left-0"
              >
                <path
                  d="M0 100 Q 50 80, 100 110 T 200 90 T 300 120 T 400 100"
                  stroke="#f9d56e"
                  fill="none"
                  strokeWidth="2"
                />
                <circle
                  cx={150 + Math.random() * 20}
                  cy={60 + Math.random() * 10}
                  r="3"
                  fill="cyan"
                  className="animate-pulse-fast"
                />
                <circle
                  cx={250 + Math.random() * 20}
                  cy={75 + Math.random() * 10}
                  r="4"
                  fill="cyan"
                  className="animate-pulse-slow"
                />
              </svg>
            </div>
          )}
          {(device.category === 'Drone' || device.category === 'Camera') && (
            <video
              src="https://storage.googleapis.com/static.aistudio.google.com/Lumina/demos/intro-video-dark.mp4"
              autoPlay
              loop
              muted
              className="w-full h-full object-cover"
            />
          )}
        </div>
        <div className="text-white space-y-1">
          <h3 className="font-bold text-sm">
            {t('devices.feedTitle', { name: device.name })}
          </h3>
          <p className="text-[11px] text-slate-300">
            {t('devices.status', 'Status: ')}
            <span className="text-green-400 font-semibold">
              {t('devices.connectedStatus', 'Connected • Live')}
            </span>
          </p>
        </div>
      </div>
    </div>
  );

  return (
    <RoddoPageShell padding="none" contentClassName="max-w-4xl px-0 roddo-devices-lane">
      <div className="space-y-3 roddo-devices-polish" data-roddo-polish-lane="2">
        {/* Header card */}
        <RoddoSectionCard className="text-center space-y-1">
          <h1 className="text-2xl font-extrabold text-primary dark:text-[#67D8E8]">
            {t('devices.title', 'Connected Devices')}
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">
            {t(
              'devices.subtitle',
              'Link sonar, cameras, and drones to power up your fishing intel.',
            )}
          </p>
        </RoddoSectionCard>

        {/* Connected devices */}
        <RoddoSectionCard className="space-y-4">
          <h2 className="text-sm font-bold mb-1 text-slate-700 dark:text-slate-200">
            {t('devices.connected', 'Currently connected')}
          </h2>
          {connectedDevices.length > 0 ? (
            renderDeviceList(connectedDevices, true)
          ) : (
            <div className="text-center p-5 rounded-[14px] bg-slate-50 dark:bg-[#101827]/80 border border-dashed border-slate-200 dark:border-[rgba(132,151,190,0.16)]">
              <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">
                {t(
                  'devices.noneConnected',
                  'No devices paired yet.',
                )}
              </p>
              <p className="text-[11px] text-slate-400 dark:text-slate-500 mt-1">
                {t(
                  'devices.scanPrompt',
                  'Scan below to discover nearby fish finders, underwater cams, and drones.',
                )}
              </p>
            </div>
          )}
        </RoddoSectionCard>

        {/* Scan / available devices */}
        <RoddoSectionCard className="space-y-4">
          <h2 className="text-sm font-bold mb-1 text-slate-700 dark:text-slate-200">
            {t('devices.findNew', 'Find new devices')}
          </h2>

          <button
            onClick={handleScan}
            disabled={isScanning}
            className="w-full p-3 rounded-[14px] bg-[#2F80ED] dark:bg-[#67D8E8] dark:text-[#07131A] text-white font-bold shadow-[0_4px_12px_rgba(89,65,37,0.06)] hover:bg-opacity-90 transition-all transform hover:-translate-y-0.5 active:scale-95 disabled:bg-slate-400 disabled:shadow-none text-sm"
          >
            {isScanning ? (
              <div className="flex items-center justify-center gap-2">
                <svg
                  className="animate-spin h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 
                      5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 
                      3 7.938l3-2.647z"
                  />
                </svg>
                {t('devices.scanning', 'Scanning for nearby devices…')}
              </div>
            ) : (
              t('devices.scan', 'Scan for devices')
            )}
          </button>

          {availableDevices.length > 0 && !isScanning && (
            <div className="pt-4 border-t border-slate-200 dark:border-[rgba(132,151,190,0.14)]">
              <h3 className="text-xs font-semibold mb-3 text-slate-600 dark:text-slate-300 uppercase tracking-wider">
                {t('devices.found', {
                  count: availableDevices.length,
                  defaultValue: 'Found {{count}} devices',
                })}
              </h3>
              {renderDeviceList(availableDevices, false)}
            </div>
          )}

          {isScanning && (
            <div className="pt-4 border-t border-slate-200 dark:border-[rgba(132,151,190,0.14)]">
              <div className="space-y-2">
                <div className="h-11 bg-slate-200 dark:bg-[#0F1828] rounded-[14px] animate-pulse" />
                <div className="h-11 bg-slate-200 dark:bg-[#0F1828] rounded-[14px] animate-pulse" />
              </div>
            </div>
          )}
        </RoddoSectionCard>
      </div>

      {showFeedModal && (
        <LiveFeedModal
          device={showFeedModal}
          onClose={() => setShowFeedModal(null)}
        />
      )}

      <style>{`
        .bg-grid-pattern {
          background-image:
            linear-gradient(to right, rgba(0,255,255,0.1) 1px, transparent 1px),
            linear-gradient(to bottom, rgba(0,255,255,0.1) 1px, transparent 1px);
          background-size: 20px 20px;
        }
        .animate-pulse-fast {
          animation: pulse 1s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
        .animate-pulse-slow {
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
      `}</style>
    </RoddoPageShell>
  );
};

export default DevicesView;
